/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { AlertController, NavController, Platform } from '@ionic/angular';
import { BehaviorSubject, from } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Storage } from '@ionic/storage-angular';
import { HttpClient } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { ConnectionsService } from './connections.service';
import { User } from '../model/user.model';
import { Market } from '@ionic-native/market/ngx';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import config from 'capacitor.config';

export interface AppVersionParam {
  versionCode: number;
  versionNumber: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _user = new BehaviorSubject<User>(null);
  private _token: string;

  constructor(
    private device: Device,
    private market: Market,
    private http: HttpClient,
    private storage: Storage,
    private platform: Platform,
    private navController: NavController,
    private alertController: AlertController,
    private connections: ConnectionsService,
    private appVersion: AppVersion,
  ) { }

  get auth() {
    return from(this.storage.get('user')).pipe(
      take(1),
      map((storedData: User) => {
        if (storedData) {
          return storedData;
        } else {
          return null;
        }
      }),
    );
  }

  get userData() {
    return this._user.asObservable();
  }

  get guardCheck() {
    return this.auth.pipe(
      switchMap((auth: User) => {
        this._token = auth.token;
        if (this._token) {
          const body = {
            device: this.getDeviceData(),
            version: environment.appVersion,
            buildNumber: environment.appBuildNumber
          };
          return this.http.post(this.connections.api + this.connections.auth.validate, body, this.connections.options(this._token));
        } else {
          return 'false';
        }
      }),
      catchError(error => {
        if (error.status === 0) {
          return 'true';
        } else {
          return 'false';
        }
      }),
      take(1),
      map((responseData: any) => {
        if (responseData.status === 'success') {
          const user: User = responseData.user;
          if (this.permissionAllowed(user.roles, 'all')) {
            user.token = this._token;
            this.storeUserData(user);
            return true;
          } else {
            this.autoRedirect('Pentaksir dan PBB', 'BLOCK');
          }
        } else if (responseData === 't') {
          this.storage.get('user').then((user: User) => {
            this._user.next(user);
          });
          return true;
        } else {
          return false;
        }
      }),
    );
  }

  get userIsAuthenticated() {
    return this.auth.pipe(
      switchMap((auth: User) => {
        this._token = auth.token;
        if (this._token) {
          const body = {
            device: this.getDeviceData(),
            version: environment.appVersion,
            buildNumber: environment.appBuildNumber
          };
          return this.http.post(this.connections.api + this.connections.auth.validate, body, this.connections.options(this._token));
        } else {
          return 'false';
        }
      }),
      catchError(error => {
        if (error.status === 0) {
          return 'true';
        } else if (error.status === 403) {
          return 'unauthorized';
        } else if (error.status === 503) {
          return 'maintenance';
        } else {
          return 'false';
        }
      }),
      take(1),
      map((responseData: any) => {
        if (responseData.status === 'success') {
          const user: User = responseData.user;
          if (this.permissionAllowed(user.roles, 'all')) {
            user.token = this._token;
            this.storeUserData(user);
            return true;
          } else {
            this.autoRedirect('Pentaksir dan PBB', 'BLOCK');
          }
        } else if (responseData === 't') {
          this.storage.get('user').then((user: User) => {
            this._user.next(user);
          });
          return true;
        } else if (responseData === 'u') {
          this.alertController.create({
            header: 'Anda Tidak Mempunyai Akses',
            message: 'Akaun anda tidak mempunyai capaian untuk akses aplikasi ini. Sila hubungi admin anda.',
            buttons: [
              {
                text: 'Ok',
                role: 'cancel',
                handler: () => {
                  this.autoLogout();
                }
              },
            ]
          }).then(alertElement => {
            alertElement.present();
          });
          return true;
        } else if (responseData === 'm') {
          // this.alertController.create({
          //   header: 'MOD PENYELENGGARAAN',
          //   message: 'Aplikasi Sedang Diselenggara',
          //   buttons: [
          //     {
          //       text: 'Ok',
          //       role: 'cancel',
          //       handler: () => {
          //         this.autoLogout();
          //       }
          //     },
          //   ]
          // }).then(alertElement => {
          //   alertElement.present();
          // });
          return true;
        } else {
          return false;
        }
      }),
    );
  }

  permissionAllowed(userRoles: string[], group: 'all' | 'pentaksir' | 'pbb' | 'pbb-admin' | 'pbb-kp' | 'pbb-school') {
    const userPbbAdmin = ['lp-admin', 'lp-operasi', 'lp-umpk', 'lp-produksi', 'lp-dasar', 'lp-pengurusan', 'jpn', 'ppd'];
    const userPbbKP = ['kpk', 'kpn', 'kpkw', 'pkw'];
    const userPbbSchool = ['pengetua', 'pkp'];
    const userPentaksir = ['pentaksir', 'ibu-bapa', 'ahli-komuniti', 'rakan-sebaya'];

    if (userRoles === undefined) {
      return false;
    }

    switch (group) {
      case 'all':
        if ([...userPbbAdmin, ...userPbbKP, ...userPbbSchool, ...userPentaksir].filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      case 'pbb':
        if ([...userPbbAdmin, ...userPbbKP, ...userPbbSchool].filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      case 'pbb-admin':
        if ([...userPbbAdmin, ...userPbbKP].filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      case 'pbb-kp':
        if (userPbbKP.filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      case 'pbb-school':
        if (userPbbSchool.filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      case 'pentaksir':
        if (userPentaksir.filter(value => userRoles.includes(value)).length > 0) {
          return true;
        } else {
          return false;
        }
      default:
        return false;
    }
  }

  getDeviceData() {
    if (this.platform.is('hybrid')) {
      return {
        cordova: this.device.cordova,
        model: this.device.model,
        platform: this.device.platform,
        uuid: this.device.uuid,
        version: this.device.version,
        manufacturer: this.device.manufacturer,
        isVirtual: this.device.isVirtual,
        serial: this.device.serial,
      };
    } else {
      return {
        cordova: false,
        model: null,
        platform: null,
        uuid: null,
        version: null,
        manufacturer: null,
        isVirtual: true,
        serial: null,
      };
    }
  }

  login(username: string, password: string) {
    const body = {
      username,
      password,
    };
    const options = {
      headers: {
        'Content-Type': 'application/json;',
      }
    };
    return this.http.post(this.connections.api + this.connections.auth.login, body, options).pipe(
      take(1),
      map((responseData: any) => {
        const user: User = responseData.user;
        if (this.permissionAllowed(user.roles, 'pentaksir')) {
          this.navController.navigateForward(['pages/assessment']);
          return responseData.user;
        } else if (this.permissionAllowed(user.roles, 'pbb')) {
          this.navController.navigateForward(['pages/pbb']);
          return responseData.user;
        } else {
          this.autoRedirect('Pentaksir dan PBB', 'PREVENT');
        }
      }),
      tap(this.storeUserData.bind(this)),
    );
  }

  autoLogin() {
    return from(this.storage.get('user')).pipe(
      take(1),
      map((storedData: User) => {
        if (!storedData) {
          return null;
        } else {
          const user: User = storedData;
          user.token = this._token;
          return user;
        }
      }),
      tap((user: User) => {
        if (user) {
          this._user.next(user);
        }
      }),
      map((user: User) => !!user),
    );
  }

  register(form: any) {
    const body = {
      role: form.role,
      name: form.name,
      email: form.email,
      ic_passport: form.ic_passport,
      contact_number: form.contact_number_initial + form.contact_number,
      correspondingAddress1: form.correspondingAddress1,
      correspondingAddress2: form.correspondingAddress2,
      schoolAddress1: form.schoolAddress1,
      schoolAddress2: form.schoolAddress2,
      state: form.state,
      password: form.password,
    };
    const options = {
      headers: {
        'Content-Type': 'application/json;',
      }
    };
    return this.http.post(this.connections.api + this.connections.auth.register, body, options).pipe(
      take(1),
      map((responseData: any) => responseData),
    );
  }

  forgot(username: string) {
    const body = {
      email: username,
    };
    const options = {
      headers: {
        'Content-Type': 'application/json;',
      }
    };
    return this.http.post(this.connections.api + this.connections.auth.forgot, body, options).pipe(
      take(1),
      map((responseData: any) => responseData),
    );
  }

  logout() {
    return this.auth.pipe(
      switchMap((auth: User) => this.http.post(this.connections.api + this.connections.auth.logout, null, this.connections.options(auth.token))),
      catchError(error => 'false'),
      take(1),
      tap(() => {
        this.autoLogout();
      }),
    );
  }

  logoutDevice(thatToken: string) {
    return this.auth.pipe(
      switchMap((auth: User) => {
        const body = {
          api_token: thatToken
        };
        return this.http.post(this.connections.api + this.connections.auth.logoutSession, body, this.connections.options(auth.token));
      }),
      take(1),
    );
  }

  autoLogout() {
    this.storage.remove('user');
    this._user.next(null);
    this.navController.navigateBack('/intro');
  }

  autoMaintenance() {
    this.navController.navigateForward('/maintenance');
  }

  terminateSession() {
    this.alertController.create({
      header: 'Oops!',
      message: `Sesi anda telah tamat atau luput.`,
      backdropDismiss: false,
      buttons: [
        {
          text: 'Ok',
          role: 'cancel',
          handler: () => {
            this.autoLogout();
          },
        },
      ],
    }).then((alertElement) => {
      alertElement.present();
    });
  }

  autoRedirect(userRole: string, action: 'PREVENT' | 'BLOCK' | 'REDIRECT') {
    if (action === 'PREVENT') {
      this.alertController.create({
        header: 'Anda Tidak Mempunyai Akses',
        message: `Halaman ini hanya boleh diakses oleh ${userRole} sahaja. Sila log masuk menggunakan akaun yang lain.`,
        buttons: [
          {
            text: 'Ok',
            role: 'cancel'
          },
        ]
      }).then(alertElement => {
        alertElement.present();
        return null;
      });
    } else if (action === 'BLOCK') {
      this.alertController.create({
        header: 'Anda Tidak Mempunyai Akses',
        message: `Halaman ini hanya boleh diakses oleh ${userRole} sahaja. Sila log masuk menggunakan akaun yang lain.`,
        buttons: [
          {
            text: 'Ok',
            role: 'cancel'
          },
        ]
      }).then(alertElement => {
        alertElement.present();
        return false;
      });
    } else if (action === 'REDIRECT') {
      this.alertController.create({
        header: 'Anda Tidak Mempunyai Akses',
        message: `Halaman ini hanya boleh diakses oleh ${userRole} sahaja. Sila log masuk menggunakan akaun yang lain.`,
        buttons: [
          {
            text: 'Ok',
            role: 'cancel',
            handler: () => {
              this.navController.navigateBack(['pages']);
            }
          },
        ]
      }).then(alertElement => {
        alertElement.present();
      });
    } else {
      this.autoLogout();
    }
  }

  handleError(error) {
    if (error.status === 401) {
      return this.terminateSession();
    } else {
      return error;
    }
  }

  updateProfile(form: any) {
    return this.auth.pipe(
      switchMap((auth: User) => {
        this._token = auth.token;
        const body = {
          name: form.name,
          // tel: form.contact_number_initial + form.contact_number,
          // address1: form.address1,
          // address2: form.address2,
          // address3: form.address3,
          birth_date: form.birth_date,
          birth_state: form.birth_state,
          gender: form.gender,
          religion: form.religion === '- TIADA MAKLUMAT -' ? null : form.religion,
          nationality: form.nationality === '- TIADA MAKLUMAT -' ? null : form.nationality,
          race: form.race === '- TIADA MAKLUMAT -' ? null : form.race,
          disability: form.disability,
          disability_type: form.disability_type,
          position: form.position,
          password: form.password ? form.password : null,
        };
        return this.http.post(this.connections.api + this.connections.auth.updateProfile, body, this.connections.options(this._token));
      }),
      take(1),
      map((responseData: any) => {
        const user: User = responseData.user;
        user.token = this._token;
        return user;
      }),
      tap(this.storeUserData.bind(this)),
    );
  }

  checkUpdates() {
    const body = {
      platform: (this.platform.is('android')) ? 'android' : (this.platform.is('ios')) ? 'ios' : 'web'
    };
    const options = {
      headers: {
        'Content-Type': 'application/json;',
      }
    };
    return this.http.post(this.connections.api + this.connections.data.version, body, options).pipe(
      take(1),
      map((responseData: AppVersionParam) => responseData),
      tap((resultData: AppVersionParam) => {
        if (this.platform.is('hybrid')) {
          this.appVersion.getVersionCode().then((version: number) => {
            // BUG: Fix version checking
            if (version < resultData.versionCode) {
              this.alertController.create({
                header: 'Terdapat Kemaskini Baharu',
                message: `Kemaskini aplikasi versi baharu (${resultData.versionNumber}) telah dikeluarkan. Sila kemaskini untuk mendapatkan versi terkini yang lebih stabil.`,
                backdropDismiss: false,
                buttons: [
                  {
                    text: 'Kemaskini',
                    handler: () => {
                      this.market.open(config.appId).then().catch(err => {
                        console.log(err);
                      });
                    }
                  }
                ]
              }).then(alertElement => {
                alertElement.present();
              });
            }
          });
        }
      }),
    );
  }

  private storeUserData(user: User) {
    this.storage.set('user', user);
    this._user.next(user);
  }

}
